import React, {
  KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
// draft js
import { DraftHandleValue, EditorState, getDefaultKeyBinding } from 'draft-js';
// draft js plugins
import Editor from '@draft-js-plugins/editor';
import { EmojiPlugin } from '@draft-js-plugins/emoji';
import createMentionPlugin, {
  MentionData,
  defaultSuggestionsFilter,
} from '@draft-js-plugins/mention';
// mui components
import { Grid } from '@mui/material';
// components
import Entry from './components/entry';
import Mention from './components/mention';
// custom hooks
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
// slices
import { setDefaultEditorHeight, setEditorHeight } from 'redux-store/slices/editor-height-slice';
// types
import PluginEditor from '@draft-js-plugins/editor/lib/Editor';
import { IStyles } from 'utils/types';
// styles
import '@draft-js-plugins/emoji/lib/plugin.css';
import '@draft-js-plugins/mention/lib/plugin.css';
import 'draft-js/dist/Draft.css';
import './styles.css';
import { HashtagEntry } from './components/hashtag-entry';
import { Hashtag } from './components/hashtag';
import { useLazyGetHashtagSuggestionsQuery } from 'redux-store/api/activity-feed';
import { mapHashtagSuggestion } from './utils';
import { IHashtagMention } from 'redux-store/api/activity-feed/hashtags.types';

export interface ICustomMentionData extends MentionData {
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface IDraftJsInputProps {
  saveHeight?: boolean;
  isError: boolean;
  placeholder: string;
  editorState: EditorState;
  autofocus?: boolean;
  inputStyles?: IStyles;
  mentionsList?: ICustomMentionData[];
  projectId?: number;
  onChange: React.Dispatch<React.SetStateAction<EditorState>>;
  onSubmit?: () => void;
  onBlur: () => void;
  emojiPlugin: EmojiPlugin;
  enableMentions?: boolean;
  enableHashtags?: boolean;
}

const DraftJsInput: React.FC<IDraftJsInputProps> = ({
  saveHeight,
  isError,
  placeholder,
  editorState,
  inputStyles,
  autofocus,
  mentionsList,
  projectId,
  onChange,
  onBlur,
  onSubmit,
  emojiPlugin,
  enableMentions = true,
  enableHashtags = true,
}) => {
  const [getHashtagSuggestions] = useLazyGetHashtagSuggestionsQuery();

  const dispatch = useAppDispatch();
  const [open, setOpen] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState(mentionsList);

  const [openHashtag, setOpenHashtag] = useState<boolean>(false);
  const [hashtagSuggestions, setHashtagSuggestions] = useState<IHashtagMention[]>([]);

  const [emojiSuggestionsOpen, setEmojiSuggestionsOpen] = useState<boolean>(false);

  const { EmojiSuggestions } = emojiPlugin;

  const mentionPlugin = useMemo(
    () =>
      createMentionPlugin({
        mentionPrefix: '@',
        mentionTrigger: '@',
        entityMutability: 'IMMUTABLE',
        mentionComponent: Mention,
      }),
    []
  );

  const hashtagPlugin = useMemo(
    () =>
      createMentionPlugin({
        mentionPrefix: '#',
        mentionTrigger: '#',
        entityMutability: 'IMMUTABLE',
        mentionComponent: Hashtag,
      }),
    []
  );

  const { MentionSuggestions } = mentionPlugin;
  const { MentionSuggestions: HashtagSuggestions } = hashtagPlugin;

  const resizeObserver = new ResizeObserver((entries) => {
    const height = entries?.[0]?.contentRect?.height;
    if (typeof height === 'number') {
      dispatch(setEditorHeight(height));
    }
  });

  const onOpenChange = useCallback((_open: boolean) => {
    setOpen(_open);
  }, []);

  const onHashtagOpenChange = useCallback((_open: boolean) => {
    setOpenHashtag(_open);

    if (!_open) {
      setHashtagSuggestions([]);
    }
  }, []);

  const onSearchChange = useCallback(({ value }: { value: string }) => {
    setSuggestions(defaultSuggestionsFilter(value, mentionsList));
  }, []);

  const onHashtagSearchChange = useCallback(
    async ({ value }: { value: string }) => {
      try {
        const result = await getHashtagSuggestions({
          search: value?.trim() || '',
          projectId,
        }).unwrap();

        const mappedData = (result?.items || []).map(mapHashtagSuggestion);
        setHashtagSuggestions(mappedData);
      } catch (error) {
        console.error('Failed to fetch hashtag suggestions', error);
        setHashtagSuggestions([]);
      }
    },
    [getHashtagSuggestions, projectId]
  );

  const editorRef = useRef<PluginEditor>(undefined);

  const keyBindingFn = (event: KeyboardEvent) => {
    if (open || openHashtag || emojiSuggestionsOpen) {
      return undefined;
    }
    if (event.key === 'Enter' && event.ctrlKey) {
      return 'enter_command';
    }
    return getDefaultKeyBinding(event);
  };

  const handleKeyCommand = (command?: string): DraftHandleValue => {
    if (command === 'enter_command' && onSubmit) {
      onSubmit();
      return 'handled';
    }
    return 'not-handled';
  };

  useEffect(() => {
    if (autofocus) {
      onChange((prev) => EditorState.moveFocusToEnd(prev));
    }
  }, []);

  useEffect(() => {
    if (saveHeight && editorRef?.current?.editor?.editorContainer) {
      resizeObserver.observe(editorRef.current.editor.editorContainer);
    }

    return () => {
      if (saveHeight) {
        resizeObserver.disconnect();
        dispatch(setDefaultEditorHeight());
      }
    };
  }, [editorRef, saveHeight]);

  return (
    <Grid
      container
      sx={
        inputStyles ?? {
          '.editor :global(.public-DraftEditor-content)': {
            minHeight: '140px',
          },
          '.DraftEditor-editorContainer': {
            outline: isError ? '1px solid #d32f2f' : '1px solid #D0D5DD',
            fontFamily:
              "'Inter', 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', 'Twemoji', sans-serif",
            '&:hover': {
              outline: '1px solid black',
            },
            '&:focus-within': {
              outline: '2px solid #1976d2',
            },
          },
        }
      }
    >
      <Editor
        editorKey="editor"
        editorState={editorState}
        onChange={onChange}
        placeholder={placeholder}
        plugins={[emojiPlugin, ...(enableMentions ? [mentionPlugin] : []), ...(enableHashtags ? [hashtagPlugin] : [])]}
        onBlur={onBlur}
        handleKeyCommand={handleKeyCommand}
        keyBindingFn={keyBindingFn}
        ref={editorRef}
      />
      <EmojiSuggestions
        onOpen={() => {
          setEmojiSuggestionsOpen(true);
        }}
        onClose={() => {
          setEmojiSuggestionsOpen(false);
        }}
      />
      {suggestions ? (
        <MentionSuggestions
          open={open}
          onOpenChange={onOpenChange}
          suggestions={suggestions}
          onSearchChange={onSearchChange}
          entryComponent={Entry}
        />
      ) : null}

      {enableHashtags && (
        <Grid
          sx={{
            '.mnw6qvm': {
              maxHeight: '200px !important',
              overflowY: 'auto !important',
            },
          }}
        >
          <HashtagSuggestions
            open={openHashtag}
            onOpenChange={onHashtagOpenChange}
            suggestions={hashtagSuggestions}
            onSearchChange={onHashtagSearchChange}
            entryComponent={HashtagEntry}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default memo(DraftJsInput);
